/**
 * Operator approval of a customer's Microsoft Entra tenant (ENTRAID-350).
 *
 * When a consent sign-in cannot be bound automatically, the backend records the
 * tenant the admin signed in from (pendingEntraTenantId) and why it stopped
 * (consentApprovalReason, one of APPROVAL_REASONS in
 * portal/backend/routes/customer-consent.mjs). An operator then approves the
 * tenant with POST /customers/{id}/consent { confirmConsent: true }. These
 * fields reach operators only (toPublicCustomer); the server enforces the
 * operator role on the approval either way.
 */

/** Plain-English sentence per approval reason, for the operator. */
export const APPROVAL_REASON_TEXT = Object.freeze({
  'starter-email-missing': 'onGuard has no trusted email for the portal user who started the consent, so it could not check the tenant against their company domain.',
  'not-admin': 'The Microsoft account that granted consent holds neither the Global Administrator nor the Privileged Role Administrator role in its tenant.',
  'domain-mismatch': "The email domain of the person who started the consent is not a verified domain of the tenant that signed in.",
  'tenant-mismatch': 'The tenant that granted consent is not the tenant that signed in.',
  'tenant-taken': 'Another onGuard customer is already connected to this tenant.',
  'tenant-locked': 'This customer already confirmed consent for a different tenant, and the tenant cannot change after that.',
  'bind-refused': 'onGuard refused to connect this tenant automatically.',
});

export const UNKNOWN_REASON_TEXT = 'onGuard could not connect this tenant automatically. The reason was not recognized.';

/** Reasons where the server will refuse an approval anyway (a held or locked tenant). */
const LIKELY_REFUSED = new Set(['tenant-taken', 'tenant-locked']);

/** The sentence for a reason, with a fallback for an unknown or missing one. */
export function approvalReasonText(reason) {
  return Object.hasOwn(APPROVAL_REASON_TEXT, reason ?? '') ? APPROVAL_REASON_TEXT[reason] : UNKNOWN_REASON_TEXT;
}

/** True when the server will most likely refuse the approval for this reason. */
export function approvalLikelyRefused(reason) {
  return LIKELY_REFUSED.has(reason);
}

/** True when the customer waits for an operator to approve the tenant that signed in. */
export function needsTenantApproval(customer) {
  return customer?.status === 'consent-pending'
    && Boolean(customer?.pendingEntraTenantId)
    && Boolean(customer?.consentApprovalReason);
}

/** The consenting admin to show: the UPN, else the object id, else null. */
export function consentingAdmin(customer) {
  return customer?.pendingTenantAdminUpn || customer?.pendingTenantAdminOid || null;
}
